import { apiBase } from './apiBase';
import { ApiError, apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Facturi emise — invoices issued from Firmio and sent to ANAF SPV.
// Mirrors backend/src/routes/issuedInvoices.ts.
// ---------------------------------------------------------------------------

export type IssuedInvoiceStatus =
  | 'DRAFT'
  | 'SENDING'
  | 'SENT'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'FAILED'
  | 'CANCELLED';

export type VatCategory = 'S' | 'Z' | 'E' | 'O';

export interface IssuedInvoiceCustomer {
  clientId: string | null;
  name: string;
  cui: string;
  regCom: string | null;
  vatPayer: boolean;
  street: string;
  city: string;
  countyCode: string;
  country: string;
  email: string | null;
}

export interface IssuedInvoiceSupplier {
  name: string;
  cui: string;
  vatPayer: boolean;
  regCom: string | null;
  street: string;
  city: string;
  countyCode: string;
  country: string;
  iban: string | null;
  bank: string | null;
  email: string | null;
  phone: string | null;
}

export interface IssuedInvoiceLine {
  id: string;
  position: number;
  name: string;
  description: string | null;
  unitCode: string;
  unitLabel: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  vatCategory: VatCategory;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
}

/** The other end of a storno: enough to name it and open it. */
export interface IssuedInvoiceStornoLink {
  id: string;
  fullNumber: string | null;
  issueDate: string;
  status: IssuedInvoiceStatus;
  totalAmount: number;
}

export interface IssuedInvoice {
  id: string;
  companyId: string;
  seriesId: string | null;
  status: IssuedInvoiceStatus;
  seriesPrefix: string | null;
  number: number | null;
  fullNumber: string | null;
  issueDate: string;
  dueDate: string;
  currency: string;
  pricesIncludeVat: boolean;
  customer: IssuedInvoiceCustomer;
  supplier: IssuedInvoiceSupplier | null;
  paymentIban: string | null;
  paymentBank: string | null;
  preparedBy: string | null;
  showPreparedBy: boolean;
  notes: string | null;
  netAmount: number;
  vatAmount: number;
  totalAmount: number;
  anaf: {
    env: string | null;
    indexIncarcare: string | null;
    idDescarcare: string | null;
    state: string | null;
    messages: string[];
    sentAt: string | null;
    acceptedAt: string | null;
    rejectedAt: string | null;
    lastCheckedAt: string | null;
  };
  hasXml: boolean;
  hasArchive: boolean;
  invoiceId: string | null;
  /** Set when this invoice IS a storno: the invoice it cancels. */
  /** Internal project tag (never on the PDF, never sent to ANAF). */
  projectId: string | null;
  project: { id: string; name: string; code: string | null } | null;
  stornoOfId: string | null;
  stornoOf: IssuedInvoiceStornoLink | null;
  /** Set when this invoice WAS stornoed: the documents that cancel it. */
  stornos: IssuedInvoiceStornoLink[];
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines: IssuedInvoiceLine[];
}

export interface IssuedInvoiceLineInput {
  name: string;
  description?: string | null;
  unit: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  vatCategory?: VatCategory;
}

export interface IssuedInvoiceCustomerInput {
  clientId?: string | null;
  name: string;
  cui: string;
  regCom?: string | null;
  vatPayer: boolean;
  street: string;
  city: string;
  countyCode: string;
  country?: string;
  email?: string | null;
}

export interface IssuedInvoiceInput {
  companyId?: string;
  seriesId?: string | null;
  issueDate: string;
  dueDate: string;
  pricesIncludeVat: boolean;
  notes?: string | null;
  /** IBAN printed on the invoice; empty = the firm's default. */
  paymentIban?: string | null;
  paymentBank?: string | null;
  /** Name after „Întocmit de”; empty = the approving user. */
  preparedBy?: string | null;
  /** false hides „Întocmit de” on the PDF (default true). */
  showPreparedBy?: boolean;
  /** Internal project tag; omit to leave it as is, null to clear. */
  projectId?: string | null;
  customer: IssuedInvoiceCustomerInput;
  lines: IssuedInvoiceLineInput[];
}

export interface IssuedInvoiceFilters {
  companyId?: string;
  projectId?: string;
  status?: IssuedInvoiceStatus;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface IssuedInvoiceListResponse {
  data: IssuedInvoice[];
  meta: { page: number; limit: number; total: number; pages: number };
  totals: Array<{ status: IssuedInvoiceStatus; count: number; totalAmount: number }>;
}

export interface InvoiceSeries {
  id: string;
  prefix: string;
  nextNumber: number;
  padding: number;
  isDefault: boolean;
  nextFullNumber: string;
}

export interface UnitOption {
  code: string;
  label: string;
  plural: string;
}

export interface CountyOption {
  code: string;
  name: string;
}

export interface ValidationResult {
  ok: boolean;
  messages: string[];
  traceId: string | null;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { ...authHeaders(), 'Content-Type': 'application/json' };
}

function query(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

const BASE = '/api/issued-invoices';

export async function listIssuedInvoices(
  filters: IssuedInvoiceFilters = {},
): Promise<IssuedInvoiceListResponse> {
  const res = await apiFetch<Envelope<IssuedInvoiceListResponse>>(
    `${BASE}${query({ ...filters })}`,
    {
      headers: authHeaders(),
    },
  );
  return res.data;
}

export async function getIssuedInvoice(id: string): Promise<IssuedInvoice> {
  const res = await apiFetch<Envelope<IssuedInvoice>>(`${BASE}/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createIssuedInvoice(input: IssuedInvoiceInput): Promise<IssuedInvoice> {
  const res = await apiFetch<Envelope<IssuedInvoice>>(BASE, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateIssuedInvoice(
  id: string,
  input: IssuedInvoiceInput,
): Promise<IssuedInvoice> {
  const res = await apiFetch<Envelope<IssuedInvoice>>(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

async function post<T>(path: string): Promise<T> {
  const res = await apiFetch<Envelope<T>>(path, { method: 'POST', headers: jsonHeaders() });
  return res.data;
}

export function validateIssuedInvoice(id: string): Promise<ValidationResult> {
  return post<ValidationResult>(`${BASE}/${encodeURIComponent(id)}/validate`);
}

export function approveIssuedInvoice(
  id: string,
): Promise<{ invoice: IssuedInvoice; validation: { ok: boolean; messages: string[] } }> {
  return post(`${BASE}/${encodeURIComponent(id)}/approve`);
}

export function refreshIssuedInvoiceState(id: string): Promise<IssuedInvoice> {
  return post<IssuedInvoice>(`${BASE}/${encodeURIComponent(id)}/refresh-state`);
}

/** The internal project tag; allowed on sent invoices too (the document does not change). */
export async function setIssuedInvoiceProject(
  id: string,
  projectId: string | null,
): Promise<IssuedInvoice> {
  const res = await apiFetch<Envelope<IssuedInvoice>>(`${BASE}/${encodeURIComponent(id)}/project`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ projectId }),
  });
  return res.data;
}

export function cancelIssuedInvoice(id: string): Promise<IssuedInvoice> {
  return post<IssuedInvoice>(`${BASE}/${encodeURIComponent(id)}/cancel`);
}

export function duplicateIssuedInvoice(id: string): Promise<IssuedInvoice> {
  return post<IssuedInvoice>(`${BASE}/${encodeURIComponent(id)}/duplicate`);
}

/**
 * The storno of an invoice: a draft with the lines reversed, already run
 * through ANAF's validator (`validation` is null when the validator could not
 * be reached). Nothing was sent — the draft still has to be approved.
 */
export function createIssuedInvoiceStorno(
  id: string,
): Promise<{ invoice: IssuedInvoice; validation: ValidationResult | null }> {
  return post(`${BASE}/${encodeURIComponent(id)}/storno`);
}

export async function listInvoiceSeries(companyId?: string): Promise<InvoiceSeries[]> {
  const res = await apiFetch<Envelope<InvoiceSeries[]>>(`${BASE}/series${query({ companyId })}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export interface InvoiceSeriesInput {
  prefix: string;
  nextNumber: number;
  padding: number;
  isDefault: boolean;
  companyId?: string;
}

/** Create a series, or change an existing one's next number / default flag. */
export async function upsertInvoiceSeries(input: InvoiceSeriesInput): Promise<InvoiceSeries> {
  const res = await apiFetch<Envelope<InvoiceSeries>>(`${BASE}/series`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function listUnitOptions(): Promise<UnitOption[]> {
  const res = await apiFetch<Envelope<UnitOption[]>>(`${BASE}/units`, { headers: authHeaders() });
  return res.data;
}

export async function listCountyOptions(): Promise<CountyOption[]> {
  const res = await apiFetch<Envelope<CountyOption[]>>(`${BASE}/counties`, {
    headers: authHeaders(),
  });
  return res.data;
}

/** The file name from a Content-Disposition header (RFC 6266), or null. */
export function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = header.match(/filename\*=(?:UTF-8|utf-8)''([^;]+)/);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through */
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/);
  return plain ? plain[1].trim() : null;
}

/**
 * PDF / XML need the Bearer header, so they are fetched as blobs (see
 * efacturaApi). `fileName` is what the server wants the download called —
 * „Factură Clean Code Culture - FIRMIO 0001.pdf”.
 */
export async function fetchIssuedInvoiceFile(
  id: string,
  kind: 'pdf' | 'xml',
): Promise<{ url: string; blob: Blob; fileName: string }> {
  const res = await fetch(`${apiBase()}${BASE}/${encodeURIComponent(id)}/${kind}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    let message = `Descărcarea a eșuat (HTTP ${res.status}).`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { error?: { message?: string; code?: string } };
      message = body.error?.message ?? message;
      code = body.error?.code;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, message, code);
  }
  const blob = await res.blob();
  const fileName =
    fileNameFromDisposition(res.headers.get('content-disposition')) ?? `factura.${kind}`;
  return { url: URL.createObjectURL(blob), blob, fileName };
}
